import React, { useState, useRef } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop, PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from 'primereact/button';

interface ImageCropperProps {
    imageSrc: string;
    onCropComplete: (blob: Blob) => void;
    onCancel: () => void;
    aspect?: number;
    loading?: boolean;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
    imageSrc,
    onCropComplete,
    onCancel,
    aspect = 1,
    loading = false,
}) => {
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [percentageCrop, setPercentageCrop] = useState<PercentCrop>();
    const imgRef = useRef<HTMLImageElement>(null);

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        const { width, height } = e.currentTarget;
        const initialCrop = centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 90,
                },
                aspect,
                width,
                height
            ),
            width,
            height
        );
        setCrop(initialCrop);
    }

    const getCroppedImg = async () => {
        if (!imgRef.current || !percentageCrop) return;

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        // Standardize output to 800x800 for optimal sharpness on high-DPI screens (2x/3x)
        // Large enough for crisp rendering at up to 260px display size on 3x DPI
        const targetSize = 800;
        canvas.width = targetSize;
        canvas.height = targetSize;

        // Calculate source coordinates
        const sx = Math.round((percentageCrop.x / 100) * image.naturalWidth);
        const sy = Math.round((percentageCrop.y / 100) * image.naturalHeight);
        const sWidth = Math.round((percentageCrop.width / 100) * image.naturalWidth);
        const sHeight = Math.round((percentageCrop.height / 100) * image.naturalHeight);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw and scale in one transformation
        ctx.drawImage(
            image,
            sx,
            sy,
            sWidth,
            sHeight,
            0,
            0,
            targetSize,
            targetSize
        );

        return new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                resolve(blob);
            }, 'image/webp', 0.95);
        });
    };

    const handleConfirm = async () => {
        try {
            const blob = await getCroppedImg();
            if (blob) {
                onCropComplete(blob);
            }
        } catch (e) {
            console.error('Error cropping image:', e);
        }
    };

    return (
        <div className="image-cropper-container" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0' }}>
            {/* Helper text */}
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
                Drag to reposition the crop area
            </p>

            {/* Crop area with padding */}
            <div className="crop-wrapper" style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#f5f5f7',
                borderRadius: '12px',
                padding: '1rem',
                overflow: 'auto',
            }}>
                <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c, p) => {
                        setCompletedCrop(c);
                        setPercentageCrop(p);
                    }}
                    aspect={aspect}
                    circularCrop={aspect === 1}
                    style={{ maxWidth: '100%' }}
                >
                    <img
                        ref={imgRef}
                        alt="Crop me"
                        src={imageSrc}
                        onLoad={onImageLoad}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '55vh',
                            display: 'block',
                            objectFit: 'contain',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                        }}
                    />
                </ReactCrop>
            </div>

            {/* Button area with separator */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
                width: '100%',
                paddingTop: '1.25rem',
                marginTop: '1.25rem',
                borderTop: '1px solid #e8e8e8',
            }}>
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={onCancel}
                    disabled={loading}
                    style={{ borderRadius: '8px', padding: '0.6rem 1.25rem' }}
                />
                <Button
                    label={loading ? "Uploading..." : "Crop & Upload"}
                    icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    onClick={handleConfirm}
                    disabled={!completedCrop || loading}
                    style={{ borderRadius: '8px', padding: '0.6rem 1.25rem' }}
                />
            </div>
        </div>
    );
};

export default ImageCropper;